import { useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { toast } from "sonner";
import PageShell from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ShieldAlert, ChevronRight, Database, Trash2, Archive, FileText, Play,
  CheckCircle2, AlertTriangle, Zap, CalendarClock,
} from "lucide-react";

/**
 * Platform Administration — System Initialization & Data Lifecycle Wizard.
 * Access: super_admin only (backend also enforces via /platform/* endpoints).
 * 10-step wizard mapped 1:1 to the spec.
 */
const RETENTION_PRESETS = [7, 15, 30];
const BULK_ACTIONS = [
  { key: "reports_only", label: "Generate Reports Only", desc: "Keep bulk parcels in place; email the summary." },
  { key: "reports_and_delete", label: "Generate Reports + Delete Bulk Data", desc: "Email reports, then purge live rows." },
  { key: "force_delete", label: "Force Delete Bulk Upload", desc: "Delete without regenerating reports (audit still logged)." },
  { key: "skip", label: "Skip Bulk Handling", desc: "Leave bulk data as-is." },
];

export default function PlatformAdminPage() {
  const [tenants, setTenants] = useState([]);
  const [tid, setTid] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [batches, setBatches] = useState([]);
  const [retention, setRetention] = useState(7);
  const [bulkAction, setBulkAction] = useState("reports_only");
  const [optimise, setOptimise] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/platform/tenants"); setTenants(data); }
      catch (e) { toast.error(e?.response?.data?.detail || "Access denied"); }
    })();
  }, []);

  const loadTenant = async (t) => {
    setTid(t.id); setAnalysis(null); setBatches([]); setPreview(null); setResult(null);
    try {
      const [a, b] = await Promise.all([
        api.get(`/platform/tenants/${t.id}/analysis`),
        api.get(`/platform/tenants/${t.id}/bulk-uploads`),
      ]);
      setAnalysis(a.data); setBatches(b.data || []);
    } catch (e) { toast.error("Failed to load tenant analysis"); }
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/tenants/${tid}/preview`, {
        retention_days: retention, bulk_action: bulkAction, optimise_db: optimise,
      });
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const execute = async () => {
    if (!window.confirm(`Platform Super Admin confirmation required.\n\nExecute lifecycle wizard for tenant ${selectedTenant?.name}?\n• Archive ${preview?.records_to_archive} completed parcels\n• Purge ${preview?.bulk_records_to_delete} bulk records\n• Optimise DB indexes\n\nThis is irreversible.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/tenants/${tid}/execute`, {
        retention_days: retention, bulk_action: bulkAction, optimise_db: optimise, confirm: true,
      });
      setResult(data);
      toast.success(`Wizard executed · archived ${data.archived}, purged ${data.bulk_purged}`);
      // refresh analysis
      const { data: a } = await api.get(`/platform/tenants/${tid}/analysis`);
      setAnalysis(a);
    } catch (e) { toast.error(e?.response?.data?.detail || "Execution failed"); }
    finally { setBusy(false); }
  };

  const selectedTenant = useMemo(() => tenants.find((t) => t.id === tid), [tenants, tid]);
  const bulkBatches50 = useMemo(() => batches.filter((b) => b.is_bulk), [batches]);

  return (
    <PageShell
      title="Platform Administration"
      subtitle="System Initialization & Data Lifecycle Wizard · Super Admin only"
      overline="Platform maintenance"
    >
      {/* Security banner */}
      <div className="mb-4 border border-red-300 bg-red-50 text-red-800 rounded-md p-3 flex items-start gap-2" data-testid="platform-banner">
        <ShieldAlert size={16} className="mt-0.5" />
        <div className="text-sm">
          <b>Restricted zone.</b> Every action here is logged with your Platform Super Admin identity.
          Destructive operations run per-tenant in the background and cannot be undone.
        </div>
      </div>

      {/* Active Data Retention — manual archive tool (Feb 2026) */}
      <RetentionTool tenants={tenants} />

      {/* Step 1 — Select Tenant */}
      <Section n={1} title="Select tenant">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="tenant-cards">
          {tenants.map((t) => (
            <button
              key={t.id}
              onClick={() => loadTenant(t)}
              data-testid={`tenant-card-${t.code}`}
              className={`text-left border rounded-md p-3 transition ${
                tid === t.id ? "border-primary bg-red-50" : "border-border bg-card hover:border-primary/40"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-display text-lg">{t.name}</span>
                <span className="mono text-[10px] bg-muted px-1.5 py-0.5 rounded">{t.code}</span>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-1 text-[10px] text-muted-foreground">
                <Stat label="Users" v={t.active_users} />
                <Stat label="Branches" v={t.branch_count} />
                <Stat label="Parcels" v={t.parcel_count} />
                <Stat label="Archive" v={t.archive_count} />
                <Stat label="Bulk" v={t.bulk_upload_count} />
                <Stat label="Reports" v={t.report_storage} />
              </div>
            </button>
          ))}
        </div>
      </Section>

      {tid && analysis && (
        <>
          {/* Step 2 — Analysis */}
          <Section n={2} title="Database analysis">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="analysis-tiles">
              {[
                ["Active", analysis.active_parcels],
                ["Delivered", analysis.delivered_parcels],
                ["Closed", analysis.closed_parcels],
                ["Pending", analysis.pending_parcels],
                ["Pickups", analysis.pickup_requests],
                ["Booked", analysis.booked_parcels],
                ["Archived", analysis.archived_parcels],
                ["Bulk batches", analysis.bulk_upload_batches],
              ].map(([l, v]) => (
                <div key={l} className="border border-border rounded-md p-2 bg-card">
                  <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{l}</div>
                  <div className="mono text-lg font-bold">{v}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2 text-sm">
              <b>DB Health:</b>
              <span className={
                analysis.database_health === "green" ? "text-emerald-700" :
                analysis.database_health === "amber" ? "text-amber-700" : "text-red-700"
              }>{analysis.database_health.toUpperCase()}</span>
              <span className="text-muted-foreground">· Space recovery candidates: <b className="mono">{analysis.space_recovery_records}</b></span>
            </div>
          </Section>

          {/* Step 3 — Retention */}
          <Section n={3} title="Retention policy">
            <div className="flex flex-wrap gap-2 items-center" data-testid="retention-choices">
              {RETENTION_PRESETS.map((d) => (
                <button key={d}
                        onClick={() => setRetention(d)}
                        data-testid={`retention-${d}`}
                        className={`border rounded-md px-3 py-1.5 text-sm ${retention === d ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>
                  Last {d} Days
                </button>
              ))}
              <div className="flex items-center gap-1 ml-2">
                <span className="text-[11px] text-muted-foreground">Custom:</span>
                <Input type="number" min="1" value={retention}
                       data-testid="retention-custom"
                       onChange={(e) => setRetention(parseInt(e.target.value) || 7)}
                       className="w-24 h-9 rounded-md font-mono" />
                <span className="text-[11px] text-muted-foreground">days</span>
              </div>
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground">
              <AlertTriangle size={11} className="inline mr-1 text-amber-600" />
              Never archived: pending, pickup requested, picked up, booked, in transit, out for delivery, SLA running.
            </div>
          </Section>

          {/* Step 4 — Bulk */}
          <Section n={4} title="Bulk entry management">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2" data-testid="bulk-actions">
              {BULK_ACTIONS.map((b) => (
                <label key={b.key}
                       className={`flex items-start gap-2 border rounded-md p-3 cursor-pointer ${bulkAction === b.key ? "border-primary bg-red-50" : "border-border"}`}>
                  <input type="radio" name="bulk-action" checked={bulkAction === b.key}
                         onChange={() => setBulkAction(b.key)}
                         data-testid={`bulk-action-${b.key}`}
                         className="mt-0.5 accent-[#BE1E1E]" />
                  <div>
                    <div className="text-sm font-semibold">{b.label}</div>
                    <div className="text-[11px] text-muted-foreground">{b.desc}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="border border-border rounded-md max-h-40 overflow-y-auto text-xs">
              {bulkBatches50.length === 0 && (
                <div className="p-3 text-center text-muted-foreground">No historical bulk uploads (>50 rows) detected for this tenant.</div>
              )}
              {bulkBatches50.map((b) => (
                <div key={b.id} className="flex justify-between border-b border-border p-2 mono">
                  <span>{b.uploaded_at?.slice(0, 16)} · {b.file_name}</span>
                  <span className="text-muted-foreground">{b.success_count} ok · {b.failure_count} err</span>
                </div>
              ))}
            </div>
          </Section>

          {/* Step 5-7 — checkboxes (Reports + Optimise) */}
          <Section n={7} title="Optimisation">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={optimise}
                     onChange={(e) => setOptimise(e.target.checked)}
                     data-testid="opt-optimise"
                     className="accent-[#BE1E1E]" />
              <Zap size={13} />
              Rebuild indexes · expire old reports · roll archive
            </label>
          </Section>

          {/* Step 8 — Preview */}
          <Section n={8} title="Preview & confirm">
            <div className="flex flex-wrap gap-2 mb-3">
              <Button variant="outline" onClick={runPreview} disabled={busy}
                      data-testid="preview-btn" className="rounded-md">
                <FileText size={13} className="mr-1" /> Preview
              </Button>
              <Button onClick={execute} disabled={busy || !preview}
                      data-testid="execute-btn"
                      className="rounded-md bg-primary text-primary-foreground shadow-brut-orange">
                <Play size={13} className="mr-1" /> Execute wizard
              </Button>
            </div>
            {preview && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="preview-tiles">
                <PreviewTile label="Current records" value={preview.current_records} />
                <PreviewTile label="Records to archive" value={preview.records_to_archive} icon={Archive} tone="emerald" />
                <PreviewTile label="Bulk to delete" value={preview.bulk_records_to_delete} icon={Trash2} tone="red" />
                <PreviewTile label="Reports to generate" value={preview.reports_to_generate} icon={FileText} tone="blue" />
                <PreviewTile label="Space recovery" value={preview.estimated_space_recovery_records} icon={Database} tone="amber" />
                <PreviewTile label="Est. execution" value={`${preview.estimated_execution_seconds}s`} />
              </div>
            )}
          </Section>

          {/* Step 9-10 — Execution result */}
          {result && (
            <Section n={9} title="Execution result">
              <div className="border rounded-md bg-emerald-50 border-emerald-300 p-3 flex gap-2 items-start" data-testid="exec-result">
                <CheckCircle2 size={16} className="text-emerald-700 mt-0.5" />
                <div className="text-sm">
                  <b>Completed.</b>{" "}
                  archived <span className="mono font-bold">{result.archived}</span>,{" "}
                  purged bulk <span className="mono font-bold">{result.bulk_purged}</span>,{" "}
                  reports expired <span className="mono font-bold">{result.reports_expired}</span>,{" "}
                  archive purged <span className="mono font-bold">{result.archive_purged}</span>,{" "}
                  indexes rebuilt <span className="mono font-bold">{result.indexes_rebuilt}</span>.
                  <div className="text-[10px] mono text-muted-foreground mt-1">{result.started_at} → {result.finished_at}</div>
                </div>
              </div>
            </Section>
          )}
        </>
      )}
    </PageShell>
  );
}

function Section({ n, title, children }) {
  return (
    <section className="mb-4 border border-border rounded-md bg-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="mono text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded">STEP {n}</span>
        <h3 className="font-display text-lg">{title}</h3>
        <ChevronRight size={13} className="text-muted-foreground" />
      </div>
      {children}
    </section>
  );
}
function Stat({ label, v }) {
  return (
    <div><div className="text-[9px] uppercase">{label}</div><div className="mono font-bold text-slate-800">{v ?? 0}</div></div>
  );
}
function PreviewTile({ label, value, icon: Icon, tone }) {
  const bg = { emerald: "bg-emerald-50 border-emerald-300", red: "bg-red-50 border-red-300",
               blue: "bg-blue-50 border-blue-300", amber: "bg-amber-50 border-amber-300" }[tone] ||
             "bg-muted border-border";
  return (
    <div className={`border rounded-md p-2 ${bg}`}>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        {Icon && <Icon size={10} />}{label}
      </div>
      <div className="mono text-lg font-bold">{value}</div>
    </div>
  );
}

/* ---------- Active Data Retention ---------- */
const PERIOD_OPTIONS = [
  { key: "today", label: "Today Only" },
  { key: "7d",    label: "Last 7 Days" },
  { key: "15d",   label: "Last 15 Days" },
  { key: "30d",   label: "Last 30 Days" },
  { key: "60d",   label: "Last 60 Days" },
  { key: "90d",   label: "Last 90 Days" },
  { key: "custom", label: "Custom Date Range" },
];
const periodLabel = (k) => PERIOD_OPTIONS.find(p => p.key === k)?.label || k;

function RetentionTool({ tenants }) {
  const [period, setPeriod] = useState("30d");
  const [startDate, setStartDate] = useState("");
  const [tenantId, setTenantId] = useState("");   // "" = all tenants
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const canSubmit = period !== "custom" || !!startDate;

  const run = async () => {
    setBusy(true);
    try {
      const payload = { period };
      if (period === "custom") payload.start_date = startDate;
      if (tenantId) payload.tenant_id = tenantId;
      const { data } = await api.post("/platform/retention/archive", payload);
      setLastResult(data);
      setConfirmOpen(false);
      toast.success(`Archived ${data.total} record${data.total === 1 ? "" : "s"} successfully.`);
      // Broadcast → Dashboard / Parcels / Reports / Pickups / Bookings pages
      // subscribe to this event to auto-refresh their data grids.
      window.dispatchEvent(new CustomEvent("mailroom:data-refresh",
        { detail: { reason: "retention-archive", ...data } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mb-4 border border-primary/40 rounded-md bg-red-50/40 p-4"
             data-testid="retention-tool">
      <div className="flex items-center gap-2 mb-1">
        <CalendarClock size={14} className="text-primary" />
        <span className="mono text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded">TOOL</span>
        <h3 className="font-display text-lg">Active Data Retention</h3>
      </div>
      <p className="text-[12px] text-muted-foreground mb-3">
        Manually archive operational data while keeping recent records active. Records are moved to Archive
        — never deleted — and remain searchable via Archive Viewer + Reports.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="overline mb-2">Keep active records</div>
          <div className="space-y-1.5" data-testid="retention-period-group">
            {PERIOD_OPTIONS.map(opt => (
              <label key={opt.key}
                     className={`flex items-center gap-2 border rounded-md px-3 py-2 cursor-pointer text-sm ${period === opt.key ? "border-primary bg-red-50" : "border-border bg-card"}`}>
                <input type="radio" name="retention-period" checked={period === opt.key}
                       onChange={() => setPeriod(opt.key)}
                       data-testid={`retention-period-${opt.key}`}
                       className="accent-[#BE1E1E]" />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
          {period === "custom" && (
            <label className="block mt-3">
              <span className="overline">Start date (records BEFORE this date will be archived)</span>
              <Input type="date" value={startDate}
                     onChange={(e) => setStartDate(e.target.value)}
                     data-testid="retention-custom-start"
                     className="mt-1 h-10 rounded-md w-full max-w-xs" />
            </label>
          )}
        </div>

        <div>
          <div className="overline mb-2">Scope</div>
          <label className="block">
            <span className="text-[11px] text-muted-foreground">Tenant (leave blank for all tenants)</span>
            <select value={tenantId}
                    onChange={(e) => setTenantId(e.target.value)}
                    data-testid="retention-tenant-select"
                    className="mt-1 h-10 w-full rounded-md border border-border bg-card px-3 text-sm">
              <option value="">All tenants</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name} · {t.code}</option>)}
            </select>
          </label>

          <div className="mt-4 text-[12px] text-muted-foreground">
            <div className="flex items-start gap-2">
              <AlertTriangle size={12} className="mt-0.5 text-amber-600" />
              <span>
                Affects Parcels, Inward Entries, Outward Entries, Inward Logs, Outward Logs,
                Bookings, Pickups, and Branch Transfers created before the selected date.
              </span>
            </div>
          </div>

          {lastResult && (
            <div className="mt-3 border border-emerald-300 bg-emerald-50 rounded-md p-3 text-[12px]"
                 data-testid="retention-result">
              <div className="flex items-center gap-2 text-emerald-800 font-semibold">
                <CheckCircle2 size={14} /> Archived {lastResult.total} record{lastResult.total === 1 ? "" : "s"} successfully.
              </div>
              <div className="mt-1 text-[11px] mono text-emerald-900/70">
                parcels {lastResult.parcels} · inward_logs {lastResult.inward_logs} ·
                outward_logs {lastResult.outward_logs} · bookings {lastResult.bookings} ·
                pickups {lastResult.pickups}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button onClick={() => setConfirmOpen(true)}
                disabled={!canSubmit || busy}
                data-testid="retention-archive-btn"
                className="rounded-md bg-primary text-primary-foreground shadow-brut-orange">
          <Archive size={13} className="mr-1" /> Archive
        </Button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="retention-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm archive</DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-2">
            <p>Archive all operational records older than:</p>
            <div className="border border-primary/40 bg-red-50 rounded-md p-3 mono text-[13px]"
                 data-testid="retention-confirm-period">
              {period === "custom" ? `Custom · before ${startDate || "—"}` : periodLabel(period)}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Scope: {tenantId ? (tenants.find(t => t.id === tenantId)?.name || tenantId) : "All tenants"}.
              Records are moved to Archive and remain searchable.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)}
                    data-testid="retention-cancel">Cancel</Button>
            <Button onClick={run} disabled={busy}
                    data-testid="retention-confirm"
                    className="bg-primary text-primary-foreground shadow-brut-orange">
              {busy ? "Archiving…" : "Archive"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
